import { ComponentRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { BookOpen, Target, GraduationCap, LoaderCircle, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import Markdown from 'react-markdown';
import { ChapterEntry, SectionEntry, TaskData, TaskEntry, TaskType } from "@/types/content-types";
import { NineStepProcessTask } from "./NineStepProcessTask";
import { config } from "@/config";
import { Dialog } from "../Dialog";
import { TaskToggle, TaskToggleInfo } from "./TaskToggle";
import { Task, TaskForm } from "./Task";
import { OpenTaskChangeListener, useChapterHandler } from "@/contexts/chapterHandler";
import { Button } from "../ui/button";
import { StarIcon } from "../StarIcon";
import { PremiumUpsellCard } from "../PremiumUpsellCard";
import { ReadDialog } from "../ReadDialog";
import { useUserContext } from "@/contexts/user";
import { useTasksInSection } from "@/contexts/appContent/appContentHooks";
import { supabase } from "@/lib/supabase/client";

interface ChapterCardProps {
  chapter: ChapterEntry
  className?: string;
  style?: React.CSSProperties;
  onStart?: () => void;
  onListen?: () => void;
  onPractice?: () => void;
  onSectionClicked?: (section: SectionEntry) => void;
  onCompleted?: () => void;
  onLastContinueJourney?: () => void;
}

const taskToggleInfos: Record<TaskType, TaskToggleInfo> = {
  simple_explore: { content: [{ text: "Utforska", Icon: BookOpen }], type: "simple_explore", sizeVariant: "small", },
  simple_test_and_reflect: { content: [{ text: "Prova och reflektera", Icon: GraduationCap }], type: "simple_test_and_reflect", sizeVariant: "small", },
  simple_practise: { content: [{ text: "Practise", Icon: Target }], type: "simple_practise", sizeVariant: "small", },
  read: undefined,
  learn: undefined,
  test: undefined,
  reflect: undefined,
  practice: undefined,
  chapter_insights: undefined,
};

type ChapterCardRef = {
  card: HTMLDivElement;
  content: HTMLDivElement;
  contentInnerWrapper: HTMLDivElement;
};

const ChapterCard = forwardRef<ChapterCardRef, ChapterCardProps>(function ChapterCard({
  className,
  style,
  chapter: chapterProp,
  onSectionClicked,
  onCompleted,
  onLastContinueJourney,
}, ref) {
  const { title, number: chapterNumber } = chapterProp;

  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentInnerWrapperRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    get card() { return cardRef.current; },
    get content() { return contentRef.current; },
    get contentInnerWrapper() { return contentInnerWrapperRef.current; },
  }));

  const taskRef = useRef<ComponentRef<typeof Task>>(null);

  const {
    chapter,
    currentSection,
    openTask,
    setOpenTask,
    taskDialogRef,
    currentSectionProgress,
    currentTasks,
    setCurrentSectionId,
    sections,
    orderedSections,
    isChapterUnlocked,
    registerOpenTaskChangeListener,
    deregisterOpenTaskChangeListener
  } = useChapterHandler();

  const tasks = useTasksInSection({ sectionId: currentSection?.id });

  const {
    permissions
  } = useUserContext();

  const currentTasksRef = useRef<typeof currentTasks>(null);

  useEffect(() => { currentTasksRef.current = currentTasks; }, [currentTasks]);

  // For now, hardcoded id of nine step process task
  const nineStepProcessTaskId = 218;

  const tryGetSpecialTask = (type: TaskType) => {
    const taskId = currentTasks?.[type]?.task?.id;
    switch (taskId) {
      case nineStepProcessTaskId:
        return <NineStepProcessTask
          key={taskId}
          taskId={nineStepProcessTaskId}
        />;
    }
  };

  const saveOpenTaskIfInputless = useCallback<OpenTaskChangeListener>((next, prev) => {
    if (
      !currentTasksRef.current?.[prev?.type] || // There is no open task
      prev.type === "chapter_insights" ||  // chapter_insights taks insight are special
      currentSection?.section?.next_section && prev.type === "practice" || // practice tasks are special if its not the last section in the chapter
      prev.type === "simple_practise" || // simple_practise tasks are completed by the continue journey button
      prev.id === nineStepProcessTaskId ||
      taskRef.current?.inputCount // input count is non-zero and not nullish
    ) return;

    // Mark tasks without inputs as completed when closed
    // (except for read tasks and nine step process (hardcoded for now))

    currentTasksRef.current[prev.type].save({
      completed: true,
      data: {
        data: undefined,
        version: 2,
      }
    });
  }, [currentSection?.section?.next_section]);

  useEffect(() => {
    registerOpenTaskChangeListener(saveOpenTaskIfInputless);
    return () => deregisterOpenTaskChangeListener(saveOpenTaskIfInputless);
  }, [registerOpenTaskChangeListener, deregisterOpenTaskChangeListener, saveOpenTaskIfInputless]);

  const showTaskToggles = (infos: TaskToggleInfo[]) => {
    return infos.map(info => {
      // Prevents showing non-existent tasks (usually chapter insights)
      if (!currentTasks[info.type]?.task?.content) return;

      return (
        <TaskToggle
          taskId={currentTasks[info.type].task.id}
          key={info.type}
          {...info}
          isUnlocked={isChapterUnlocked}
          onClick={() => {
            setOpenTask(info.type);
          }}
        />
      );
    });
  };

  return (
    <Card
      style={style}
      ref={cardRef}
      className={cn(
        "p-6",
        isChapterUnlocked
          ? "bg-gradient-card border-step-current/20 hover:border-step-current/40"
          : "bg-muted border-border text-muted-foreground",
        className,
      )}
    >
      <CardContent className="p-0 relative" ref={contentRef}>
        {!chapter.has_access && <div className="absolute w-full z-10 top-1/2 -translate-y-1/2">
          <PremiumUpsellCard />
        </div>}

        <div ref={contentInnerWrapperRef} className={`${!chapter.has_access && "blur-[1px]"}`}>
          <Dialog
            scrollContainerRef={taskDialogRef}
            open={openTask != null}
            onOpenChange={(open) => {
              if (open) return;
              saveOpenTaskIfInputless(null, openTask);
              setOpenTask(null);
            }}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            {/* If there is a special task, that is rendered instead of the default task */}
            {tryGetSpecialTask(openTask?.type) ??
              <>
                <Task
                  ref={taskRef}
                  taskId={openTask?.id}
                  scrollTarget={openTask?.scrollTarget}
                />
                <div className="flex flex-row justify-end w-full pr-2 gap-4">
                  {openTask?.type === "simple_explore" && permissions.has("full-text.read") && (
                    <ReadDialog
                      text={currentSection.section.text}
                      trigger={
                        <Button>
                          Läs
                        </Button>
                      }
                    />
                  )}
                  {openTask?.type === "simple_test_and_reflect" && (
                    <TestAndReflectButtons
                      locked={!permissions.has("old-tasks")}
                      sectionId={currentSection.id}
                    />
                  )}
                  {/* simple_practise renders its continue journey button inside the task itself */}
                  {openTask?.type !== "simple_practise" && (
                    <Button
                      variant="step-current"
                      onClick={() => {
                        saveOpenTaskIfInputless(null, openTask);
                        setOpenTask(null);
                      }}
                    >
                      Stäng
                    </Button>
                  )}
                </div>
              </>
            }
          </Dialog>

          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h3 className={cn(
                "text-xl font-semibold mb-2 transition-colors",
                isChapterUnlocked ? "text-foreground" : "text-muted-foreground"
              )}>
                <Markdown>{`Kapitel ${chapterNumber}: ${title}`}</Markdown>
              </h3>
              <div className={cn(
                "text-sm transition-colors",
                isChapterUnlocked ? "text-muted-foreground" : "text-muted-foreground/60"
              )}>
                {!sections && (
                  <div className="text-xl flex flex-row items-center">
                    Hämtar underrubriker
                    <LoaderCircle className="animate-spin" />
                  </div>
                )}
                {orderedSections && orderedSections.map((section, index) => {
                  const bgColor = section.completed ? "bg-success/10" : "bg-step-current/10";
                  const textColor = section.completed ? "text-success" : "text-step-current";
                  const hoverTextColor = section.completed ? "hover:text-success" : "hover:text-step-current";
                  // If there is no previous sections, then unlock this section
                  const prevSectionCompleted = sections.get(section.section.prev_section)?.completed ?? true;
                  const isSectionUnlocked = config.unlockAll || (
                    isChapterUnlocked &&
                    section.section.has_access &&
                    (index === 0 || prevSectionCompleted)
                  );
                  const isSelect = section?.id === currentSection?.section?.id;
                  return (
                    <div
                      key={section.id}
                      className={cn(
                        "transition-all flex items-center gap-2 select-none",
                        isSectionUnlocked && (isSelect && isChapterUnlocked
                          ? `text-md font-medium ${bgColor} px-3 py-1 rounded-lg ${textColor}`
                          : isSectionUnlocked
                            ? `cursor-pointer ${hoverTextColor} hover:underline`
                            : "opacity-60"),
                        section.completed && "text-success",
                        !isSectionUnlocked && "text-muted-foreground/60 opacity-60"
                      )}
                      onClick={() => {
                        if (!isSectionUnlocked) return;
                        setCurrentSectionId(section.id);
                        onSectionClicked?.(section.section);
                      }}
                    >
                      <StarIcon className="p-1" />
                      {section.section.title}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">
                Framsteg: {currentSection.completedTaskCount}/{currentSection.taskCount}
              </span>
              <span className="text-sm font-medium text-foreground">
                {Math.round(currentSectionProgress)}%
              </span>
            </div>
            <Progress
              value={currentSectionProgress}
              className={cn(
                "h-2 transition-all duration-500",
                isChapterUnlocked ? "opacity-100" : "opacity-50"
              )}
            />
          </div>

          {/* Section indicators */}
          <div className="mb-6 space-y-3">

            {/* 3 columns */}
            <div className="grid grid-cols-3 gap-2">
              {
                showTaskToggles([
                  taskToggleInfos.simple_explore,
                  taskToggleInfos.simple_test_and_reflect,
                  taskToggleInfos.simple_practise,
                ])
              }
            </div>
          </div>
          {/* </ChapterHandlerProvider> */}
        </div>
      </CardContent>
    </Card>
  );
});

export { ChapterCard };

function TestAndReflectButtons({ locked, sectionId }) {
  const [tasks, setTasks] = useState<Record<"test" | "reflect", Pick<TaskEntry, "content" | "type" | "id"> & { task_data: TaskData }>>(undefined);
  useEffect(() => {
    if (locked) return;
    (async () => {
      const { data, error } = await supabase
        .from("tasks_view")
        .select("id, type, content, completed_tasks(task_data)")
        .eq("section_id", sectionId)
        .in("type", ["test", "reflect"])
        .limit(2);

      if (error) {
        console.error("fetch test and reflect failed", error);
        return;
      }

      setTasks(
        data.reduce((result, row) => {
          const { completed_tasks, content, id, type } = row;
          result[row.type] = { task_data: completed_tasks?.[0], content, id, type };
          console.log(result);
          return result;
        }, {} as typeof tasks)
      );
    })();
  }, [locked, sectionId]);
  return (
    !locked && <>
      {/* Old Test/Experiment */}
      <Dialog
        trigger={
          <Button>
            Experiment
          </Button>
        }
      >
        <TaskForm
          title={"Experiment: :section-title"}
          content={tasks?.test?.content}
          values={tasks?.test?.task_data?.data}
          disabled={true}
        />
      </Dialog>

      {/* Old Reflect */}
      <Dialog
        trigger={
          <Button>
            Reflektera
          </Button>
        }
      >
        <TaskForm
          title={"Reflektera: :section-title"}
          content={tasks?.reflect?.content}
          values={tasks?.reflect?.task_data?.data}
          disabled={true}
        />
      </Dialog>
    </>
  );
}